import { useEffect, useRef, useState } from 'react'

const colors = ['#33cccc', '#e66699']
const messages = {
  session_changed: 'The session changed. Updating…',
  no_active_session: 'No active session for this Smily.',
  graph_denied: 'You can only view your own session chart.',
  easytech_session_expired: 'Session expired. Sign in again.',
  sign_in_required: 'Sign in again to view charts.',
  retry_later: 'Waiting to update…',
}
function Plot({ points, kind, maximum, color, label }) {
  const [sample, setSample] = useState(null)
  const prefix = kind === 'Speed' ? 'RPM' : 'Nm'
  const x = index => 42 + index * 540 / Math.max(1, points.length - 1)
  const y = value => 190 - Math.min(1, value / maximum) * 164
  const selected = sample === null ? null : points[sample]
  return <div>
    <p style={{ color }}>{label} · scale 0–{maximum} {prefix}</p>
    <svg viewBox='0 0 610 224' role='img' aria-label={`${label} over time`} style={{ width: '100%', display: 'block' }}>
      {[0, 0.5, 1].map(level => <g key={level}>
        <line x1='42' x2='582' y1={y(level * maximum)} y2={y(level * maximum)} stroke='#526477' />
        <text x='2' y={y(level * maximum) - 4} fill='white' fontSize='10'>{level * maximum}</text>
      </g>)}
      {points.map((point, index) => <rect key={index} x={x(index) - 2} y={y(point[`max${kind}Value`])} width='4' height={190 - y(point[`max${kind}Value`])} fill={color} opacity='0.25' />)}
      <polyline fill='none' stroke={color} strokeWidth='2' points={points.map((point, index) => `${x(index)},${y(point[`max${kind}Value`])}`).join(' ')} />
      {points.map((point, index) => <circle key={index} cx={x(index)} cy={y(point[`max${kind}Value`])} r='5' fill={color} tabIndex='0' role='button' aria-label={`${point.timestamp}: ${point[`min${kind}Value`]}–${point[`max${kind}Value`]} ${prefix}`} onFocus={() => setSample(index)} onPointerEnter={() => setSample(index)} onClick={() => setSample(index)}>
        <title>{point.timestamp}: {point[`min${kind}Value`]}–{point[`max${kind}Value`]} {prefix}</title>
      </circle>)}
      {points.length > 0 && <>
        <text x='42' y='215' fill='white' fontSize='11'>{new Date(points[0].timestamp).toLocaleTimeString()}</text>
        <text x='582' y='215' textAnchor='end' fill='white' fontSize='11'>{new Date(points[points.length - 1].timestamp).toLocaleTimeString()}</text>
      </>}
    </svg>
    {selected && <p style={{ fontSize: 13 }}>{new Date(selected.timestamp).toLocaleString()} · {selected[`min${kind}Value`]}–{selected[`max${kind}Value`]} {prefix}</p>}
  </div>
}
export function CourseSmilyGraphs({ world, state, button, direct = false }) {
  const section = useRef(null)
  useEffect(() => { if (state.graphStationId) section.current?.scrollIntoView({ block: 'nearest' }) }, [state.graphStationId])
  const [stationId, setStationId] = useState(state.graphStationId ? String(state.graphStationId) : '')
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const station = state.stations.find(value => String(value.id) === stationId)
  const sessionId = station?.currentSession?.sessionId
  useEffect(() => { setStationId(state.graphStationId ? String(state.graphStationId) : '') }, [state.selected, state.graphStationId])
  useEffect(() => {
    setResult(null)
    setError('')
    if (!stationId || !sessionId) return
    let disposed = false
    let timer
    const controller = new AbortController()
    world.courseRoom.graphOpen = true
    const update = async () => {
      if (disposed) return
      if (world.courseRoom.inflight) { timer = setTimeout(update, 1200); return }
      world.courseRoom.inflight = true
      setBusy(true)
      try {
        const data = await world.courseRoom.request(`rooms/${state.selected}/stations/${stationId}/graph`, { signal: controller.signal })
        if (!disposed) { setResult(data); setError('') }
      } catch (failure) {
        if (!disposed) {
          setResult(null)
          setError(messages[failure.message] || 'Data unavailable. Retrying in a few seconds.')
          if ([401, 403].includes(failure.status) && failure.message !== 'graph_denied') world.courseRoom.fail(failure)
        }
      } finally {
        world.courseRoom.inflight = false
        if (!disposed) { setBusy(false); timer = setTimeout(update, 5000) }
      }
    }
    update()
    return () => { disposed = true; controller.abort(); clearTimeout(timer); world.courseRoom.graphOpen = false }
  }, [world, state.selected, stationId, sessionId])
  return <section ref={section} aria-label='Grafici dei singoli Smily' style={{ borderTop: '1px solid #526477', marginTop: 20, paddingTop: 16 }}>
    <strong>{direct ? `Chart · ${station?.name || `Smily ${stationId}`}` : 'Smily charts'}</strong>
    {!direct && <>
    <label htmlFor='course-graph-station' style={{ display: 'block', marginTop: 12 }}>Station</label>
    <select id='course-graph-station' value={stationId} onChange={event => setStationId(event.target.value)} style={{ ...button, width: '100%', marginTop: 8 }}>
      <option value=''>Select a Smily</option>
      {state.stations.map(value => <option key={value.id} value={value.id}>{value.name || `Smily ${value.id}`} · {value.currentSession ? 'active session' : 'no session'}</option>)}
    </select>
    </>}
    {station && !sessionId && <p>No active session for this Smily.</p>}
    {station && sessionId && <p>{station.name || `Smily ${station.id}`} · session {result?.sessionId ?? sessionId}</p>}
    {busy && <p role='status'>Updating chart…</p>}
    {error && <p role='alert'>{error}</p>}
    {result && !result.points.length && <p>This session has no samples yet.</p>}
    {(stationId || result) && <>
      <Plot key={`${stationId}-speed`} points={result?.points || []} kind='Speed' maximum={40000} color={colors[0]} label='Speed' />
      <Plot key={`${stationId}-torque`} points={result?.points || []} kind='Torque' maximum={10} color={colors[1]} label='Torque' />
      <p style={{ fontSize: 13 }}>{result?.points.length || 0} samples · updates every 5 seconds. Select a point to view minimum and maximum values.</p>
    </>}
  </section>
}
